import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '@ui/Input';
import Slider from '@ui/Slider';
import Checkbox from '@ui/Checkbox';
import Button from '@ui/Button';
import TextArea from '@ui/TextArea';
import { cx } from 'cva';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import useUserProfile from '@/hooks/useUserProfile';

const schemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Email must be a valid email id'),
});

const schemaCommon = yup.object().shape({
  // firstName: yup
  //   .string()
  //   .required('First name is required')
  //   .min(2, 'First name must be at least 2 characters')
  //   .max(50, 'First name must be at most 50 characters'),
  // lastName: yup
  //   .string()
  //   .required('Last name is required')
  //   .min(2, 'Last name must be at least 2 characters')
  //   .max(50, 'Last name must be at most 50 characters'),
  // email: yup
  //   .string()
  //   .required('Email is required')
  //   .email('Email must be a valid email id'),
  amount: yup
    .number()
    .required()
    .min(1, 'Minimum amount is $1')
    .typeError('Minimum amount is $1'),
  tip: yup.array().of(yup.number().min(0).max(100)).required(),
  billingPeriod: yup.string().required('Billing period is required'),
  doDedicate: yup.boolean().notRequired().nullable(),
  dedicatedToName: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to name is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToEmail: yup.string().when('doDedicate', {
    is: true,
    then: yup
      .string()
      .required('Dedicated to email is required')
      .email('Dedicated to email must be a valid email'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToMessage: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to message is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  isAnonymous: yup.boolean().notRequired().nullable(),
  shareNameAndEmail: yup.boolean().notRequired().nullable(),
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
});

function StripeContributionForm(props) {
  const {
    post,
    amount = '',
    onSubmit,
    isLoggedIn,
    fundraisingOrg = {},
  } = props;

  const [frequencyTab, setFrequencyTab] = useState('One Time');
  const [amountTab, setAmountTab] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);

  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const user = useUserProfile();

  const hasCompanyPayment =
    user?.activeCommunity?.companyPaymentGatewayConfig?.gatewayInfo
      ?.paymentMethodId;

  const schema = isLoggedIn()
    ? schemaCommon
    : schemaPublic.concat(schemaCommon);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      amount: amount,
      tip: [30],
      // tip: [0],
      doDedicate: false,
      dedicatedToName: '',
      dedicatedToEmail: '',
      dedicatedToMessage: '',
      isAnonymous: false,
      shareNameAndEmail: true,
      agreeTerms: false,
      billingPeriod: 'one time',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
    setValue,
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
  } = form;

  const [amountVal, nekiTip, doDedicate] = watch([
    'amount',
    'tip',
    'doDedicate',
  ]);
  const contributionAmount = Math.max(0, Number(amountVal));
  const tipAmount = Number(
    Math.max(0, (contributionAmount * nekiTip) / 1000).toFixed(2),
  );

  const isRecurringcontribution = () => frequencyTab !== 'One Time';

  const handleFrequencyTabChange = (tab) => {
    setFrequencyTab(tab);
    setValue('billingPeriod', `${tab.toLowerCase()}`);
  };

  const handleAmountTabChange = (tab) => {
    setAmountTab(tab);
    setValue('amount', `${tab}`);
  };

  const handleFormSubmit = (data) => {
    onSubmit(data, isRecurringcontribution());
  };

  const handleUpdateCurrentLevel = (level) => {
    setCurrentLevel(level);
    setValue('amount', level.quantity);
  };

  return (
    <div
      className={cx(
        'flex flex-1 flex-col justify-start',
        isLessThanMediumScreen && 'mb-20',
      )}>
      {/* <Typography variant="h1" className="mb-6">
        Make a contribution
      </Typography> */}
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col justify-start gap-8 lg:flex-row lg:justify-between">
        <div className="flex w-full shrink-0 grow-0 flex-col gap-6 lg:w-6/12">
          <Typography variant="body1" color="textSecondary" className="">
            Contribute your own funds to support the beneficiary.
          </Typography>

          <Typography>
            Donation Match and tax benefits are not available.
          </Typography>

          {!isLoggedIn() && (
            <>
              <div className="flex flex-col gap-4 lg:flex-row">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="First Name"
                      required
                      {...rest}
                      error={errors.firstName?.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Last Name"
                      required
                      {...rest}
                      error={errors.lastName?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name="email"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Input
                    label="Email Id"
                    required
                    {...rest}
                    error={errors.email?.message}
                  />
                )}
              />
            </>
          )}

          <Controller
            name="amount"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="CONTRIBUTION AMOUNT"
                placeholder="Enter an Amount"
                required
                {...rest}
                adornment={
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="!font-medium">
                    $
                  </Typography>
                }
                error={errors.amount?.message}
              />
            )}
          />

          <Controller
            name="tip"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <div>
                <Typography variant="h5" className="mb-2.5 uppercase">
                  Neki Service Tip
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className="mb-8">
                  NEKI has a 0% platform fee for the contributors and
                  appreciates the generosity of contributors like you to support
                  and enhance our services.
                </Typography>
                <Slider {...rest} minLabel="0%" maxLabel="10%" />
              </div>
            )}
          />
        </div>
        <div className="flex flex-1 flex-col justify-between gap-2.5">
          <div className="flex flex-col gap-2.5">
            <Typography variant="h5" className="uppercase">
              Summary
            </Typography>

            <div className="flex">
              <Typography variant="body1" color="textSecondary">
                Your Contribution
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className="ml-auto">
                ${contributionAmount.toFixed(2)}
              </Typography>
            </div>
            <div className="flex border-b">
              <Typography variant="body1" color="textSecondary">
                NEKI Tip
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className="ml-auto">
                ${tipAmount.toFixed(2)}
              </Typography>
            </div>
            <div className="flex">
              <Typography variant="body1" color="textPrimary">
                Total
              </Typography>
              <Typography
                variant="body1"
                color="textPrimary"
                className="ml-auto">
                ${(tipAmount + contributionAmount).toFixed(2)}
              </Typography>
            </div>

            <div className="flex items-center gap-4">
              <Controller
                name="doDedicate"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-bold">
                Dedicate this contribution to someone
              </Typography>
            </div>

            {doDedicate && (
              <div className="flex flex-col gap-4">
                <Controller
                  name="dedicatedToName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Name"
                      required
                      {...rest}
                      error={errors.dedicatedToName?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToEmail"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Email Id"
                      required
                      {...rest}
                      error={errors.dedicatedToEmail?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToMessage"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <TextArea
                      label="Message"
                      required
                      {...rest}
                      error={errors.dedicatedToMessage?.message}
                    />
                  )}
                />
              </div>
            )}

            <div className="flex items-center gap-4">
              <Controller
                name="isAnonymous"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-bold">
                Hide my name for this contribution
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:gap-4">
            <Controller
              name="agreeTerms"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-4">
                    <Checkbox
                      {...rest}
                      error={errors.agreeTerms?.message}
                      className="border-text-disabled font-extrabold"
                    />
                    <Typography
                      variant="body2"
                      color="textDisabled"
                      className="font-bold">
                      I agree to the{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/terms-and-conditions"
                        className="text-primary hover:underline">
                        Terms of Service
                      </Typography>{' '}
                      and{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/privacy-policy"
                        className="text-primary hover:underline">
                        Privacy Policy
                      </Typography>
                    </Typography>
                  </div>
                  <Typography
                    variant="body2"
                    color="error"
                    className={!errors.agreeTerms && 'hidden'}>
                    Please agree to proceed
                  </Typography>
                </div>
              )}
            />

            <Button
              type="submit"
              variant="filled"
              color="primary"
              className={cx(
                'flex w-full items-center justify-center',
                doDedicate && 'mb-4',
              )}>
              Contribute
            </Button>
            <Typography
              variant="body1"
              color="textSecondary"
              className="flex items-center justify-center font-bold">
              Powered by{' '}
              <img src="/assets/icons/stripe-logo.svg" alt="stripe-logo" />
            </Typography>
          </div>
        </div>
      </form>
      <Typography
        variant="caption"
        color="textSecondary"
        className="mb-2 mt-8 pb-14 text-justify lg:pb-0">
        Your contribution will be processed securely via Stripe. The funds will
        go to your association’s Stripe-connected account, subject to Stripe’s
        terms and conditions. Because this is a personal
        fundraiser—contributions are not tax deductible. The beneficiary
        receives the funds, minus the payment processor’s standard fee of 2.9% +
        $0.30. This fundraiser is hosted on Neki’s platform, which empowers
        people to do good with ease and transparency. While we do not
        independently vet every personal fundraiser, we provide tools and
        support to help hosts and givers stay connected, accountable, and
        informed. In the rare event that a fundraiser is canceled or funds
        cannot be delivered, we will work with Stripe to resolve the issue in
        line with their policies. Thank you for showing up for someone in
        need—your kindness truly makes a difference.
      </Typography>
    </div>
  );
}

export default StripeContributionForm;
